import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const MethodRow = ({ m, onChange, onRemove }) => (
  <div className="bg-white rounded-2xl border border-[#A8B59A]/20 p-5 grid md:grid-cols-12 gap-3 items-center" data-testid={`pm-row-${m.key}`}>
    <div className="md:col-span-3">
      <Input value={m.label} onChange={(e) => onChange({ ...m, label: e.target.value })} className="rounded-xl" data-testid={`pm-label-${m.key}`} />
      <p className="text-[11px] text-[#5E4D5A] mt-1" data-testid={`pm-scope-${m.key}`}>{m.meetup_only ? "Local Meetup only · no username needed" : "Shipping & meetup · asks for email/username"}</p>
    </div>
    <Input value={m.instructions} placeholder="Optional note shown to customers" onChange={(e) => onChange({ ...m, instructions: e.target.value })} className="md:col-span-6 rounded-xl" data-testid={`pm-instr-${m.key}`} />
    <label className="md:col-span-2 flex items-center gap-2 text-sm">
      <Switch checked={m.enabled} onCheckedChange={(v) => onChange({ ...m, enabled: v })} data-testid={`pm-enabled-${m.key}`} />
      {m.enabled ? "Shown" : "Hidden"}
    </label>
    {m.key === "cash_meetup" ? <span className="md:col-span-1" /> : <button onClick={onRemove} className="md:col-span-1 text-[#59394F] justify-self-end" data-testid={`pm-remove-${m.key}`}><Trash2 size={14} /></button>}
  </div>
);

const SettingToggle = ({ auth, field, title, help, onText, offText, testid, switchId }) => {
  const [on, setOn] = useState(null);
  useEffect(() => { (async () => setOn((await api.get("/admin/checkout-settings", await auth())).data[field]))(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (v) => {
    try {
      setOn((await api.put("/admin/checkout-settings", { [field]: v }, await auth())).data[field]);
      toast.success(`${title}: ${v ? onText : offText}`);
    } catch (e) {
      toast.error(`Could not update ${title}`);
    }
  };
  if (on === null) return null;
  return (
    <div className="bg-white rounded-2xl border border-[#A8B59A]/20 p-5 mb-4 flex flex-wrap items-center justify-between gap-4 max-w-3xl" data-testid={testid}>
      <div>
        <p className="font-serif text-xl">{title}</p>
        <p className="text-sm text-[#5E4D5A] mt-1">{help}</p>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={on} onCheckedChange={toggle} data-testid={switchId} />
        {on ? onText : offText}
      </label>
    </div>
  );
};

const MeetupToggle = ({ auth }) => (
  <SettingToggle auth={auth} field="meetup_enabled" testid="meetup-setting" switchId="meetup-enabled-switch" title="Local Meetup (free)" onText="Offered" offText="Off"
    help="When on, customers can choose a free local meetup instead of $8 shipping. A phone number is required." />
);

const CustomerEmailToggle = ({ auth }) => (
  <div className="mb-10">
    <SettingToggle auth={auth} field="customer_emails_enabled" testid="customer-emails-setting" switchId="customer-emails-switch" title="Customer order confirmations" onText="Sending" offText="Off"
      help="When on, customers automatically get a 'We've Received Your Order!' email. Admin alerts are always sent." />
  </div>
);

export default function AdminPayments() {
  const { getToken } = useAuth();
  const [methods, setMethods] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const auth = async () => ({ headers: { Authorization: `Bearer ${await getToken()}` } });

  useEffect(() => { (async () => setMethods((await api.get("/admin/payment-methods", await auth())).data))(); }, []);

  const add = () => {
    const key = newLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    if (key.length < 2 || methods.some((m) => m.key === key)) return toast.error("Enter a new, unique method name");
    setMethods([...methods, { key, label: newLabel.trim(), enabled: false, instructions: "", meetup_only: false, needs_handle: true }]);
    setNewLabel("");
  };

  const save = async () => {
    try {
      setMethods((await api.put("/admin/payment-methods", { methods }, await auth())).data);
      toast.success("Payment methods saved");
    } catch (e) {
      toast.error("Could not save — check the method names");
    }
  };

  return (
    <div className="p-8 lg:p-12">
      <p className="text-xs uppercase tracking-[0.3em] text-[#5E6B51]">Checkout</p>
      <h1 className="font-serif text-4xl mt-2 mb-3">Payment preferences</h1>
      <p className="text-sm text-[#5E4D5A] max-w-2xl mb-10">
        Customers pick one of the <strong>shown</strong> methods at checkout. Nothing is charged or requested automatically — you invoice and confirm payment manually. If every method is hidden, checkout simply says you'll be in touch with payment details.
      </p>
      <MeetupToggle auth={auth} />
      <CustomerEmailToggle auth={auth} />
      <div className="space-y-3 mb-6">
        {methods.map((m, i) => (
          <MethodRow key={m.key} m={m} onChange={(nm) => setMethods(methods.map((x, j) => (j === i ? nm : x)))} onRemove={() => setMethods(methods.filter((_, j) => j !== i))} />
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Add a method (e.g., Zelle)" className="rounded-xl max-w-xs" data-testid="pm-new-label" />
        <Button variant="outline" onClick={add} className="rounded-xl" data-testid="pm-add"><Plus size={14} className="mr-1" />Add</Button>
        <Button onClick={save} className="rounded-xl bg-[#59394F] hover:bg-[#432A3B] text-white" data-testid="pm-save">Save changes</Button>
      </div>
    </div>
  );
}
